import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { SupplierReturnFilters } from '@/features/purchasing-returns/api'
import { ReturnList } from '@/features/purchasing-returns/return-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the supplier return list. `returns.new.tsx` and
 * `returns.$returnId.index.tsx` are CHILDREN of this route in flat file
 * routing — without the Outlet below their URLs render this list instead of
 * themselves (apps/company/AGENTS.md).
 */
export const Route = createFileRoute('/_authenticated/purchasing/returns')({
  validateSearch: (search: Record<string, unknown>): SupplierReturnFilters => ({
    supplier: typeof search.supplier === 'string' && search.supplier ? search.supplier : undefined,
    warehouse:
      typeof search.warehouse === 'string' && search.warehouse ? search.warehouse : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'supplier_return.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: PurchasingReturnsRoute,
})

function PurchasingReturnsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <ReturnList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/purchasing/returns', search: next })}
    />
  )
}
